import type { SearchHit } from "@shared/search.ts";
import type { MapsPlaceHint } from "@shared/maps-url.ts";
import type { PhraseWrite } from "@shared/phrase-write.ts";
import { TripDocumentSchema, type ThaiPhrase, type TripDocument } from "@shared/schema.ts";
import { readTripEtag, tripWriteHeaders, wrapTripWrite } from "@shared/trip-etag.ts";

async function parse(res: Response): Promise<Record<string, unknown>> {
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw Object.assign(new Error(String(data.error ?? `Request failed (${res.status})`)), {
      status: res.status,
      data,
    });
  }
  return data;
}

function responseEtag(res: Response, data: Record<string, unknown>): string | null {
  return readTripEtag(data, res.headers.get("x-trip-etag") ?? res.headers.get("etag"));
}

function readTrip(data: Record<string, unknown>, label: string): TripDocument {
  const parsed = TripDocumentSchema.safeParse(data.trip);
  if (!parsed.success) {
    throw Object.assign(new Error(`${label} did not include a usable trip.`), {
      status: 200,
      data,
      malformed: true,
    });
  }
  return parsed.data;
}

export async function getSession() {
  return (await parse(await fetch("/api/session", { credentials: "same-origin" }))) as {
    authenticated: boolean;
    role?: "family" | "owner";
    displayName?: string | null;
  };
}

export async function login(password: string, displayName?: string) {
  return (await parse(
    await fetch("/api/login", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password, displayName }),
    }),
  )) as { role: "family" | "owner" };
}

export async function logout() {
  await fetch("/api/logout", { method: "POST", credentials: "same-origin" });
}

export async function fetchTrip() {
  const res = await fetch("/api/trip", { credentials: "same-origin" });
  const data = await parse(res);
  const empty = Boolean(data.empty);
  return {
    empty,
    trip: empty || data.trip == null ? null : readTrip(data, "Trip load"),
    etag: responseEtag(res, data),
    role: data.role as "family" | "owner" | undefined,
  };
}

export async function saveTrip(trip: TripDocument, etag: string) {
  const res = await fetch("/api/trip", {
    method: "PUT",
    credentials: "same-origin",
    headers: tripWriteHeaders(etag),
    body: JSON.stringify(wrapTripWrite(trip, etag)),
  });
  const data = await parse(res);
  return { trip: readTrip(data, "Trip save"), etag: responseEtag(res, data), conflict: res.status === 409 };
}

export async function saveTripPhrases(thaiPhrases: ThaiPhrase[], etag: string, baseRevision?: number) {
  return saveTripPhraseWrite({ action: "merge", phrases: thaiPhrases }, etag, baseRevision);
}

export async function saveTripPhraseWrite(write: PhraseWrite, etag: string, baseRevision?: number) {
  const res = await fetch("/api/phrases", {
    method: "PUT",
    credentials: "same-origin",
    headers: tripWriteHeaders(etag),
    body: JSON.stringify({ ...write, etag, baseRevision }),
  });
  if (res.status === 404) {
    throw Object.assign(new Error("Phrase save is not available on this deploy."), {
      status: 404,
      data: await res.json().catch(() => ({})),
    });
  }
  const data = await parse(res);
  return { trip: readTrip(data, "Phrase save"), etag: responseEtag(res, data) };
}

export async function repairHotelPins() {
  return parse(
    await fetch("/api/places/repair", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
    }),
  );
}

export async function importSeed(seed: unknown, commit: boolean, etag?: string) {
  return parse(
    await fetch("/api/import", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ seed, commit, etag }),
    }),
  );
}

export async function importLocal() {
  return parse(await fetch("/api/import/local", { method: "POST", credentials: "same-origin" }));
}

export async function search(q: string) {
  return (await parse(await fetch(`/api/search?q=${encodeURIComponent(q)}`, { credentials: "same-origin" }))) as {
    hits: SearchHit[];
  };
}

export async function postPayment(body: unknown) {
  return parse(
    await fetch("/api/payments", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function postSettlement(body: unknown) {
  return parse(
    await fetch("/api/settlements", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function postRefund(body: unknown) {
  return parse(
    await fetch("/api/refunds", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function uploadAttachment(file: File, linkedEntityIds: string[]) {
  const form = new FormData();
  form.set("file", file);
  form.set("linkedEntityIds", linkedEntityIds.join(","));
  return parse(await fetch("/api/attachments", { method: "POST", credentials: "same-origin", body: form }));
}

export async function lookupHotelLocations() {
  return parse(
    await fetch("/api/places/lookup", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
    }),
  );
}

export async function readMapsPlace(url: string, signal?: AbortSignal) {
  return (await parse(
    await fetch("/api/places/from-maps", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url }),
      signal,
    }),
  )) as { hint: MapsPlaceHint };
}

export async function draftPhrase(english: string, kind: "allergy" | "dietary" | "phrase") {
  return parse(
    await fetch("/api/phrases/draft", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ english, kind }),
    }),
  ) as Promise<{
    english?: string;
    candidates: Array<{
      thai: string;
      romanization: string;
      backTranslation: string;
      pitfalls: string;
      provider?: "dictionary" | "google" | "anthropic" | "openai";
    }>;
  }>;
}

export async function fetchLive(forDate?: string) {
  const q = forDate ? `?forDate=${encodeURIComponent(forDate)}` : "";
  return parse(await fetch(`/api/live${q}`, { credentials: "same-origin" }));
}

export async function fetchPack() {
  return parse(await fetch("/api/pack", { credentials: "same-origin" }));
}

export async function fetchExport(files = false) {
  return parse(await fetch(`/api/export${files ? "?files=1" : ""}`, { credentials: "same-origin" }));
}

export async function restoreBackup(payload: unknown, confirm: boolean) {
  return parse(
    await fetch("/api/restore", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...((payload as object) ?? {}), confirm }),
    }),
  );
}
